import DownloadIcon from 'assets/icons/DownloadIcon'
import { useFinishProcess } from 'hooks/process/useFinishProcess/useFinishProcess'
import { useFetchAllProcesses } from 'hooks/process/useGetAllProcessCurrentCompany/useGetAllProcessCurrentCompany'
import useReportDownloaderByExcel from 'hooks/process/useGetExcelReportByProcess/use-get-excel-report-by-process'
import { useFetchProcesses } from 'hooks/process/useGetProcess/useGetProcess'
import { useNotification } from 'lib/context/notification.context'
import { useZipReportJob } from 'lib/context/zip-report-job.context'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { platformAdminRoutes } from 'router/routes'
import { getProcessDetail } from 'services/common/process/createProcess/create-process-service'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import { ActionViewDownloadAddUsers } from 'views/components/UI/BluesiteTable/Actions/GridActions'
import { StrokedButton } from 'views/components/UI/Buttons'
import Loader from 'views/components/UI/Loader'
import { SubtitleBold } from 'views/components/UI/Text'
import { MainLayout } from 'views/layouts'

import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import SelectSearch from '../../Analytics/selectSearch'
import { HEADERS_SENT_PROCESSES } from '../constants'
import { DownloadProcess, IDownloadProcess } from '../store'
import ModalDateFilter from './components/ModalDateFilter'
import { DeliveredTableContainer } from './delivered-table-styled'
import {
  Container,
  EmptyStateText,
  HeaderActions,
  HeaderWrapper,
  LoaderWrapper,
  TableHeading,
  TableCount,
  TableTitle,
  ViewWrapper,
  WrapperEmptyState,
} from './styled'

const getUnansweredRatio = (unansweredUsers: number, totalUsers: number) => (
  <span>
    <span style={{ color: '#C6016B', fontWeight: 700 }}>{unansweredUsers}</span>/{totalUsers}
  </span>
)

const DeliveredProcesses = () => {
  const navigate = useNavigate()
  const { handleFinishProcess } = useFinishProcess()
  const { getError, getWarning } = useNotification()
  const { job: zipJobStatus, starting: isCreatingZipJob, start: startZipJob } = useZipReportJob()

  const { listProcess, fetchProcesses, loadingListProcesses, paginationListProcess } = useFetchProcesses()
  const { fetchProcesses: fetchAllProcess } = useFetchProcesses()
  const { allProcess, fetchAllProcesses } = useFetchAllProcesses()

  const { fetchGetReportsByExcel, loading: isGeneratingSummary } = useReportDownloaderByExcel()
  const [loading, setLoading] = useState(false)
  const [processSelected, setProcessSelected] = useState<number | ''>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [processIds, setProcessIds] = useState<Set<number>>(new Set())
  const [isDisabled, setIsDisabled] = useState(false)
  const [showModalDateFilter, setShowModalDateFilter] = useState(false)

  useEffect(() => {
    if (processSelected === '') {
      fetchAllProcesses()
    }
  }, [processSelected, fetchAllProcesses])

  useEffect(() => {
    if (processSelected) {
      fetchProcesses({ sent: true, processId: processSelected, evaluatedOnly: false })
    } else {
      fetchProcesses({ sent: true, evaluatedOnly: false })
    }
  }, [allProcess, processSelected, fetchProcesses, fetchAllProcess])

  useEffect(() => {
    setIsDisabled(processIds.size > 0)
  }, [processIds])

  function handleViewProcess(id: number) {
    navigate(platformAdminRoutes.DetailProcessDelivered, { state: { id } })
  }

  const handleSelectProcess = (processId: number) => {
    setProcessSelected(processId)
    setCurrentPage(1)
  }

  const handleDateFilterButtonClick = () => {
    setShowModalDateFilter(true)
  }

  const handleDownloadSelectedProcessesZip = async () => {
    if (processIds.size < 1) {
      return
    }

    if (processIds.size > 30) {
      getWarning('El límite máximo para descargar como ZIP es 30 procesos')
      return
    }

    await startZipJob(Array.from(processIds))
  }

  const handleDownloadReports = async ({
    processId,
    processName,
    useBetesa,
    useCompetence,
    use360,
    isForAllProcess,
    initDate,
    endDate,
    suppressWarning,
  }: DownloadProcess) => {
    if (isGeneratingSummary) {
      return { status: 'error' }
    }

    return fetchGetReportsByExcel({
      processId: processId ?? 0,
      useBetesa,
      useCompetence,
      use360,
      processName: processName ?? '',
      isForAllProcess,
      initDate: initDate ?? '',
      endDate: endDate ?? '',
      processIds: Array.from(processIds) ?? [],
      suppressWarning,
    })
  }

  const handleDownloadReportsAllProcesses = () => {
    handleDownloadReports({
      useBetesa: false,
      useCompetence: false,
      use360: false,
      isForAllProcess: true,
    })
  }

  const handleAddUsers = useCallback(
    (processId: number) => {
      getProcessDetail(processId)
        .then((response: any) => {
          setLoading(false)
          handleFinishProcess(response.process)
          navigate(platformAdminRoutes.CreateUsers, { state: { id: processId, isSendedProcess: true } })
        })
        .catch(() => {
          getError('Ha ocurrido un error al cargar este proceso')
          setLoading(false)
        })
    },
    [navigate, getError, handleFinishProcess]
  )

  const handlePagination = useCallback(
    (page: number) => {
      if (processSelected) return
      fetchProcesses({ sent: true, page, evaluatedOnly: false })
      setCurrentPage(page)
    },
    [fetchProcesses, setCurrentPage, processSelected]
  )

  const formattedProcesses = listProcess.map((process: any) => ({
    ...process,
    unanswered_ratio: getUnansweredRatio(process.unanswered_users || 0, process.count_users || 0),
  }))

  return (
    <MainLayout>
      <Container>
        <HeaderWrapper>
          <HeaderActions>
            <StrokedButton
              onClick={handleDownloadSelectedProcessesZip}
              type="button"
              disabled={processIds.size < 1 || isCreatingZipJob || zipJobStatus?.status === 'pending' || zipJobStatus?.status === 'processing'}
            >
              {isCreatingZipJob ? 'Preparando ZIP...' : 'Descargar reportes en ZIP'}
            </StrokedButton>
            <StrokedButton onClick={handleDateFilterButtonClick} type="button">
              Descargar resumen por rango de fechas
            </StrokedButton>
            <StrokedButton onClick={handleDownloadReportsAllProcesses} type="button">
              <DownloadIcon />
              {isGeneratingSummary
                ? 'Generando resumen...'
                : isDisabled
                ? 'Generar resumen de los procesos seleccionados'
                : 'Generar resumen de todos los procesos'}
            </StrokedButton>
          </HeaderActions>
        </HeaderWrapper>
        {!loadingListProcesses && !loading ? (
          <>
            {listProcess?.length > 0 ? (
              <>
                <DeliveredTableContainer>
                  <TableHeading>
                    <TableTitle>Procesos creados</TableTitle>
                    <ViewWrapper>
                      <SubtitleBold>Visualizar:</SubtitleBold>
                      <SelectSearch
                        showCheckboxes={false}
                        noChip={true}
                        label="Todos los procesos"
                        options={allProcess || []}
                        value={processSelected}
                        onChange={(e: any) => {
                          handleSelectProcess(e as number)
                        }}
                        limitTags={1}
                      />
                      <HighlightOffIcon
                        style={{ cursor: 'pointer', color: 'gray' }}
                        onClick={() => {
                          setProcessSelected('')
                        }}
                      />
                    </ViewWrapper>
                    <TableCount>Total de registros: {paginationListProcess.totalResults}</TableCount>
                  </TableHeading>
                  <BluesiteTable
                    data={formattedProcesses}
                    headers={HEADERS_SENT_PROCESSES}
                    tableStyle="process"
                    searchBar={false}
                    searchLabel=""
                    ActionComponent={ActionViewDownloadAddUsers}
                    totalPages={paginationListProcess.totalPages}
                    currentPage={currentPage}
                    handlePagination={handlePagination}
                    checks={true}
                    showNumbers={true}
                    totalItems={paginationListProcess.totalResults}
                    showTotalItems={false}
                    selectedItems={processIds}
                    setSelectedItems={(e) => setProcessIds(e)}
                    extraDataAction={{
                      onAddUsers: (e: number) => handleAddUsers(e),
                      onViewProcess: (e: number) => handleViewProcess(e),
                      onDownloadProcess: (e: IDownloadProcess) =>
                        handleDownloadReports({
                          processId: e.id,
                          processName: e.name,
                          useBetesa: e.betesaTest,
                          useCompetence: e.competencesTest,
                          use360: e.test_360,
                        }),
                      isDisabled,
                    }}
                  />
                </DeliveredTableContainer>
              </>
            ) : (
              <WrapperEmptyState>
                <EmptyStateText>Esta compania no tiene procesos creados</EmptyStateText>
              </WrapperEmptyState>
            )}
          </>
        ) : (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        <ModalDateFilter
          showConfirmModal={showModalDateFilter}
          setShowModal={setShowModalDateFilter}
          selectedProcessCount={processIds.size}
          downloadFunction={async (dates) => {
            const result = await handleDownloadReports({
              ...dates,
              useBetesa: false,
              useCompetence: false,
              use360: false,
              isForAllProcess: true,
              suppressWarning: true,
            })
            return result instanceof ArrayBuffer ? 'success' : result?.status === 'empty' ? 'empty' : 'error'
          }}
        />
      </Container>
    </MainLayout>
  )
}

export default DeliveredProcesses
